import { 
  Cart, 
  CartItem, 
  CartService, 
  CartValidationResult, 
  CartItemCreateData,
  createCartItemId,
  createEmptyCart,
  calculateCartTotals,
  VatSettings
} from '../types';
import { shippingService } from './shippingService';
import { getVatSettings, calculateVatAmount } from './firebase';

/**
 * CartService - Handles all cart operations with localStorage persistence
 * Implements singleton pattern for global access
 */
class CartServiceImpl implements CartService {
  private static instance: CartServiceImpl;
  private readonly STORAGE_KEY = 'the-frosting-room-cart';
  private readonly CART_VERSION = '1.0.0';
  private readonly MAX_ITEMS = 50; // Maximum items in cart
  // MAX_QUANTITY removed to support quantity tiers > 10 for desserts

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance of CartService
   */
  public static getInstance(): CartServiceImpl {
    if (!CartServiceImpl.instance) {
      CartServiceImpl.instance = new CartServiceImpl();
    }
    return CartServiceImpl.instance;
  }

  /**
   * Save cart to localStorage
   */
  public saveCart(cart: Cart): void {
    try {
      const cartData = {
        ...cart,
        lastUpdated: new Date().toISOString(),
        version: this.CART_VERSION
      };
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cartData));
      
      // Dispatch storage event for multi-tab sync (only in browser environment)
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        try {
          window.dispatchEvent(new StorageEvent('storage', {
            key: this.STORAGE_KEY,
            newValue: JSON.stringify(cartData),
            storageArea: localStorage
          }));
        } catch (error) {
          // Ignore storage event errors in test environment
          console.warn('Failed to dispatch storage event:', error);
        }
      }
    } catch (error) {
      console.error('Failed to save cart to localStorage:', error);
      throw new Error('Failed to save cart. Please try again.');
    }
  }

  /**
   * Load cart from localStorage
   */
  public loadCart(): Cart | null {
    try {
      const cartData = localStorage.getItem(this.STORAGE_KEY);
      
      if (!cartData) {
        return null;
      }

      const parsedCart = JSON.parse(cartData);
      
      // Migrate cart if needed
      if (parsedCart.version !== this.CART_VERSION) {
        return this.migrateCart(parsedCart);
      }

      // Convert date strings back to Date objects
      const cart: Cart = {
        ...parsedCart,
        lastUpdated: new Date(parsedCart.lastUpdated),
        items: parsedCart.items.map((item: any) => ({
          ...item,
          addedAt: new Date(item.addedAt),
          updatedAt: new Date(item.updatedAt),
          deliveryDate: item.deliveryDate ? new Date(item.deliveryDate) : undefined
        }))
      };

      // Validate loaded cart
      if (!this.validateCart(cart)) {
        console.warn('Invalid cart data detected, creating new cart');
        return createEmptyCart();
      }

      return cart;
    } catch (error) {
      console.error('Failed to load cart from localStorage:', error);
      return createEmptyCart();
    }
  }

  /**
   * Clear cart from localStorage
   */
  public clearCart(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      
      // Dispatch storage event for multi-tab sync (only in browser environment)
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        try {
          window.dispatchEvent(new StorageEvent('storage', {
            key: this.STORAGE_KEY,
            newValue: null,
            storageArea: localStorage
          }));
        } catch (error) {
          // Ignore storage event errors in test environment
          console.warn('Failed to dispatch storage event:', error);
        }
      }
    } catch (error) {
      console.error('Failed to clear cart from localStorage:', error);
      throw new Error('Failed to clear cart. Please try again.');
    }
  }

  /**
   * Validate cart data integrity
   */
  public validateCart(cart: Cart): boolean {
    try {
      // Check if cart has required properties
      if (!cart || typeof cart !== 'object') {
        return false;
      }

      if (!Array.isArray(cart.items)) {
        return false;
      }

      // Check if cart has too many items
      if (cart.items.length > this.MAX_ITEMS) {
        return false;
      }

      // Validate each cart item
      for (const item of cart.items) {
        if (!this.validateCartItem(item)) {
          return false;
        }
      }

      // Validate totals
      const calculatedTotals = calculateCartTotals(cart.items);
      if (Math.abs(cart.totalItems - calculatedTotals.totalItems) > 0.01) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Cart validation error:', error);
      return false;
    }
  }

  /**
   * Validate individual cart item
   */
  private validateCartItem(item: CartItem): boolean {
    try {
      // Check required fields
      if (!item.id || !item.productId || !item.name) {
        return false;
      }

      // Check quantity limits (minimum only, no maximum to support quantity tiers > 10)
      if (item.quantity < 1) {
        return false;
      }

      // Check price fields
      if (typeof item.basePrice !== 'number' || item.basePrice < 0) {
        return false;
      }

      if (typeof item.unitPrice !== 'number' || item.unitPrice < 0) {
        return false;
      }

      if (typeof item.totalPrice !== 'number' || item.totalPrice < 0) {
        return false;
      }

      // Check dates
      if (!(item.addedAt instanceof Date) || !(item.updatedAt instanceof Date)) {
        return false;
      }

      // Validate image URLs if they are required
      if (item.hasImageOnCake && !item.cakeImageUrl) {
        console.warn('Cart item validation: hasImageOnCake is true but cakeImageUrl is missing');
        return false;
      }

      if (item.hasSampleImage && !item.sampleImageUrl) {
        console.warn('Cart item validation: hasSampleImage is true but sampleImageUrl is missing');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Cart item validation error:', error);
      return false;
    }
  }

  /**
   * Migrate cart data from older versions
   */
  public migrateCart(oldCart: any): Cart {
    try {
      console.log('Migrating cart from version:', oldCart.version || 'unknown');
      
      // For now, just create a new empty cart
      // In the future, we can add specific migration logic here
      return createEmptyCart();
    } catch (error) {
      console.error('Cart migration error:', error);
      return createEmptyCart();
    }
  }

  /**
   * Create a new cart item from product data
   */
  public createCartItem(data: CartItemCreateData): CartItem {
    const now = new Date();
    
    // Calculate additional prices (these are per unit)
    const additionalPrices = data.flavorAdditionalPrice + data.fillingAdditionalPrice + data.icingAdditionalPrice + data.addOnsAdditionalPrice + data.matchingCupcakesAdditionalPrice + data.imageOnCakeAdditionalPrice + (data.baseColorAdditionalPrice || 0);
    
    // For quantity tiers (desserts), use the tier total price directly
    // Otherwise, calculate base price * quantity
    let baseTotalPrice: number;
    if (data.quantityTierTotalPrice !== undefined) {
      // Quantity tier: tier price is already the total for the quantity
      baseTotalPrice = data.quantityTierTotalPrice;
    } else {
      // Regular pricing: base price per unit * quantity
      baseTotalPrice = data.basePrice * data.quantity;
    }
    
    // Total price = base total + (additional prices * quantity)
    // Additional prices are per unit, so multiply by quantity
    const totalPrice = baseTotalPrice + (additionalPrices * data.quantity);
    
    // Unit price is total price divided by quantity (for display purposes)
    const unitPrice = totalPrice / data.quantity;

    return {
      id: createCartItemId(),
      productId: data.productId,
      name: data.name,
      imageUrl: data.imageUrl,
      size: data.size,
      flavors: data.flavors,
      shape: data.shape,
      layers: data.layers,
      filling: data.filling,
      icing: data.icing,
      baseColor: data.baseColor,
      pipingColor: data.pipingColor,
      specialInstructions: data.specialInstructions,
      addOns: data.addOns || [],
      matchingCupcakes: data.matchingCupcakes || [],
      hasImageOnCake: data.hasImageOnCake || false,
      cakeImageUrl: data.cakeImageUrl,
      hasSampleImage: data.hasSampleImage || false,
      sampleImageUrl: data.sampleImageUrl,
      deliveryOption: data.deliveryOption,
      deliveryDate: data.deliveryDate,
      timeWindowStart: data.timeWindowStart,
      timeWindowEnd: data.timeWindowEnd,
      quantity: data.quantity,
      basePrice: data.basePrice,
      flavorAdditionalPrice: data.flavorAdditionalPrice,
      fillingAdditionalPrice: data.fillingAdditionalPrice,
      icingAdditionalPrice: data.icingAdditionalPrice,
      addOnsAdditionalPrice: data.addOnsAdditionalPrice || 0,
      matchingCupcakesAdditionalPrice: data.matchingCupcakesAdditionalPrice || 0,
      imageOnCakeAdditionalPrice: data.imageOnCakeAdditionalPrice || 0,
      baseColorAdditionalPrice: data.baseColorAdditionalPrice || 0,
      unitPrice: Math.round(unitPrice * 100) / 100,
      totalPrice: Math.round(totalPrice * 100) / 100,
      addedAt: now,
      updatedAt: now
    };
  }

  /**
   * Check if two cart items are identical (for merging)
   */
  public areItemsIdentical(item1: CartItem, item2: CartItem): boolean {
    return (
      item1.productId === item2.productId &&
      item1.size === item2.size &&
      JSON.stringify(item1.flavors.sort()) === JSON.stringify(item2.flavors.sort()) &&
      item1.shape === item2.shape &&
      item1.layers === item2.layers &&
      item1.filling === item2.filling &&
      item1.icing === item2.icing &&
      item1.baseColor === item2.baseColor &&
      item1.pipingColor === item2.pipingColor &&
      item1.specialInstructions === item2.specialInstructions &&
      item1.hasImageOnCake === item2.hasImageOnCake &&
      item1.cakeImageUrl === item2.cakeImageUrl &&
      item1.hasSampleImage === item2.hasSampleImage &&
      item1.sampleImageUrl === item2.sampleImageUrl &&
      item1.deliveryOption === item2.deliveryOption &&
      item1.deliveryDate?.getTime() === item2.deliveryDate?.getTime() &&
      item1.timeWindowStart === item2.timeWindowStart &&
      item1.timeWindowEnd === item2.timeWindowEnd
    );
  }

  /**
   * Find duplicate item in cart
   */
  public findDuplicateItem(cart: Cart, newItem: CartItem): CartItem | null {
    return cart.items.find(item => this.areItemsIdentical(item, newItem)) || null;
  }

  /**
   * Get detailed validation result
   */
  public getValidationResult(cart: Cart): CartValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      // Check cart structure
      if (!cart || typeof cart !== 'object') {
        errors.push('Cart data is invalid');
        return { isValid: false, errors, warnings };
      }

      if (!Array.isArray(cart.items)) {
        errors.push('Cart items must be an array');
        return { isValid: false, errors, warnings };
      }

      // Check item count
      if (cart.items.length > this.MAX_ITEMS) {
        errors.push(`Cart cannot have more than ${this.MAX_ITEMS} items`);
      }

      // Check each item
      cart.items.forEach((item, index) => {
        if (!this.validateCartItem(item)) {
          errors.push(`Item ${index + 1} is invalid`);
        }

        // Quantity limit check removed to support quantity tiers > 10 for desserts
      });

      // Check totals
      const calculatedTotals = calculateCartTotals(cart.items);
      if (Math.abs(cart.totalItems - calculatedTotals.totalItems) > 0.01) {
        warnings.push('Cart totals may be outdated');
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings
      };
    } catch (error) {
      errors.push('Validation failed due to unexpected error');
      return { isValid: false, errors, warnings };
    }
  }

  /**
   * Get cart statistics
   */
  public getCartStats(cart: Cart): {
    totalItems: number;
    totalValue: number;
    averageItemValue: number;
    mostExpensiveItem: CartItem | null;
    cheapestItem: CartItem | null;
  } {
    if (cart.items.length === 0) {
      return {
        totalItems: 0,
        totalValue: 0,
        averageItemValue: 0,
        mostExpensiveItem: null,
        cheapestItem: null
      };
    }

    const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    const totalValue = cart.items.reduce((sum, item) => sum + item.totalPrice, 0);
    const averageItemValue = totalValue / cart.items.length;

    const mostExpensiveItem = cart.items.reduce((max, item) => 
      item.unitPrice > max.unitPrice ? item : max
    );

    const cheapestItem = cart.items.reduce((min, item) => 
      item.unitPrice < min.unitPrice ? item : min
    );

    return {
      totalItems,
      totalValue: Math.round(totalValue * 100) / 100,
      averageItemValue: Math.round(averageItemValue * 100) / 100,
      mostExpensiveItem,
      cheapestItem
    };
  }

  /**
   * Calculate cart totals with VAT settings
   */
  public async calculateCartTotalsWithVat(
    items: CartItem[], 
    customerInfo?: { deliveryOption?: 'collection' | 'delivery'; address?: { postalCode?: string } },
    couponDiscount: number = 0
  ): Promise<Pick<Cart, 'totalItems' | 'subtotal' | 'vat' | 'vatPercentage' | 'isInclusive' | 'shipping' | 'couponDiscount' | 'total'>> {
    try {
      // Calculate basic totals first
      const totalItems = items.reduce((sum, item) => {
        return sum + (item?.quantity || 0);
      }, 0);
      
      const subtotal = items.reduce((sum, item) => {
        return sum + (item?.totalPrice || 0);
      }, 0);

      // Get VAT settings and calculate VAT
      console.log('🛒 CartService.calculateCartTotalsWithVat - About to calculate VAT for subtotal:', subtotal);
      const { vatAmount, vatPercentage, isInclusive } = await calculateVatAmount(subtotal);
      console.log('🛒 CartService.calculateCartTotalsWithVat - VAT calculation result:', { vatAmount, vatPercentage, isInclusive });
      
      // Calculate shipping based on delivery option and postal code
      let shipping = 0;
      
      console.log('🛒 CartService.calculateCartTotalsWithVat - customerInfo:', {
        deliveryOption: customerInfo?.deliveryOption,
        postalCode: customerInfo?.address?.postalCode,
        hasAddress: !!customerInfo?.address
      });
      
      if (customerInfo?.deliveryOption === 'delivery' && customerInfo?.address?.postalCode) {
        console.log('🚚 Calling shippingService.calculateShipping for delivery with postal code:', customerInfo.address.postalCode);
        // Use delivery tiers to calculate shipping cost
        shipping = await shippingService.calculateShipping(
          customerInfo.address.postalCode,
          'delivery'
        );
        console.log('💰 Shipping calculated:', shipping);
      } else if (customerInfo?.deliveryOption === 'collection') {
        console.log('📦 Collection selected - shipping is FREE');
        // Collection is always free
        shipping = 0;
      } else {
        console.log('⚠️ Using fallback shipping logic - subtotal:', subtotal);
        // Fallback to original logic for backward compatibility
        // shipping = subtotal > 50 ? 0 : 5; 
        console.log('💰 Fallback shipping calculated:', shipping);
      }
      
      // Calculate total based on VAT type and apply coupon discount
      let total: number;
      if (isInclusive) {
        // For inclusive pricing: total = subtotal (VAT already included) - coupon discount
        total = subtotal + shipping - couponDiscount;
      } else {
        // For exclusive pricing: total = subtotal + VAT + shipping - coupon discount
        total = subtotal + vatAmount + shipping - couponDiscount;
      }
      
      // Ensure total doesn't go below 0
      total = Math.max(0, total);
      
      return {
        totalItems,
        subtotal: Math.round(subtotal * 100) / 100,
        vat: vatAmount,
        vatPercentage,
        isInclusive,
        shipping,
        couponDiscount,
        total: Math.round(total * 100) / 100
      };
    } catch (error) {
      console.error('Error calculating cart totals with VAT:', error);
      // Fallback to basic calculation without VAT
      const basicTotals = calculateCartTotals(items, couponDiscount);
      return {
        ...basicTotals,
        vat: 0,
        vatPercentage: 0,
        isInclusive: false
      };
    }
  }

  /**
   * Update cart shipping based on customer information
   */
  public async updateCartShipping(
    cart: Cart, 
    customerInfo: { deliveryOption?: 'collection' | 'delivery'; address?: { postalCode?: string } }
  ): Promise<Cart> {
    console.log('🛒 CartService.updateCartShipping called:', {
      cartItems: cart.items.length,
      customerInfo
    });

    try {
      const updatedTotals = await this.calculateCartTotalsWithVat(cart.items, customerInfo, cart.couponDiscount || 0);
      console.log('🛒 Updated totals:', updatedTotals);
      
      const updatedCart = {
        ...cart,
        ...updatedTotals
      };
      console.log('🛒 Final updated cart:', updatedCart);
      
      return updatedCart;
    } catch (error) {
      console.error('❌ Error updating cart shipping:', error);
      return cart;
    }
  }

  /**
   * Update cart with VAT calculations (preserves existing shipping)
   */
  public async updateCartWithVat(cart: Cart): Promise<Cart> {
    try {
      console.log('🛒 CartService.updateCartWithVat - Called with cart shipping:', cart.shipping);
      
      // Only recalculate VAT, preserve existing shipping calculation
      const subtotal = cart.items.reduce((sum, item) => {
        return sum + (item?.totalPrice || 0);
      }, 0);
      
      const { vatAmount, vatPercentage, isInclusive } = await calculateVatAmount(subtotal);
      const totalAmount = subtotal + vatAmount + cart.shipping; // Use existing shipping
      
      console.log('🛒 CartService.updateCartWithVat - Preserving shipping:', {
        existingShipping: cart.shipping,
        subtotal,
        vatAmount,
        totalAmount
      });
      
      return {
        ...cart,
        subtotal,
        vat: vatAmount,
        vatPercentage,
        isInclusive,
        total: totalAmount,
        lastUpdated: new Date()
      };
    } catch (error) {
      console.error('Error updating cart with VAT:', error);
      // If there's an error, return cart with VAT disabled
      return {
        ...cart,
        vat: 0,
        vatPercentage: 0,
        isInclusive: false,
        total: cart.subtotal + cart.shipping - (cart.couponDiscount || 0)
      };
    }
  }
}

// Export singleton instance
export const cartService = CartServiceImpl.getInstance();

// Export class for testing
export { CartServiceImpl };
